import { inject, Injectable } from '@angular/core';
import { NgxAimService } from '../ngx-aim.service';
import { BehaviorSubject, Observable, catchError, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CurrentUser } from '../models';
import { AppStateService } from './app-state.service';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { GoogleAnalyticsService } from './google-analytics.service';
import { GAEventAction } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  ngxAimService = inject(NgxAimService);
  http = inject(HttpClient);
  appStateService = inject(AppStateService);
  router = inject(Router);
  baseUrl = this.ngxAimService.getApiUrl();
  messageService = inject(MessageService);
  gaService = inject(GoogleAnalyticsService);

  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public getCurrentUser() {
    return this.currentUserSubject.value;
  }

  constructor() {
    if (this.appStateService.isBrowser) {
      const userFromStorage = localStorage.getItem('currentUser');
      if (userFromStorage) {
        this.currentUserSubject.next(JSON.parse(userFromStorage));
      }
    }
  }

  login(email: string, password: string): Observable<CurrentUser> {
    return this.http.post<CurrentUser>(`${this.baseUrl}/auth/login`, { email, password })
      .pipe(
        tap((currentUser) => this.onSuccessfulLogin(currentUser)),
        catchError((err) => this.onUnsuccessfulLogin(err),
        ));
  }

  logout() {
    if (this.appStateService.isBrowser) {
      this.http.post<void>(`${this.baseUrl}/auth/logout`, {}).subscribe();
      localStorage.removeItem('currentUser');
      this.currentUserSubject.next(null);
      this.router.navigateByUrl('/');
      this.appStateService.setUserPriceLevel('ActiveePrice');
    }
  }

  onSuccessfulLogin(currentUser: CurrentUser) {
    localStorage.setItem('currentUser', JSON.stringify(currentUser));
    this.currentUserSubject.next(currentUser);
    this.appStateService.initUser();
    this.gaService.emitEvent(GAEventAction.LOGIN);
  }

  onUnsuccessfulLogin(err: any) {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
    return throwError(() => err);
  }

  createGuestUser(firstName: string, lastName: string, email: string,): Observable<CurrentUser> {
    return this.http.post<CurrentUser>(`${this.baseUrl}/auth/create-guest-user`, {
      firstName,
      lastName,
      email
    }).pipe(
      tap((currentUser) => this.onSuccessfulLogin(currentUser)),
      catchError((err) => this.onUnsuccessfulLogin(err),
      ));
  }

  showSessionExpiredMessage() {
    this.messageService.add({
      severity: 'warn',
      summary: 'Session expired. Please log in.',
      key: 'app-toast',
      life: 8000
    });
  }
}
